import bunyan from 'bunyan';
import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';
import * as Sentry from '@sentry/node';

let logger: Logger;

const SENSITIVE_FIELD_KEY_SUBSTRINGS = [
  'token',
  'password',
  'secret',
  'credentials',
  'environmentVariable',
];

function isJWT(str: string): boolean {
  // JWT typically follows the pattern: xxx.yyy.zzz
  // where each part is base64url encoded
  const jwtPattern = /^[A-Za-z0-9_-]{2,}(?:\.[A-Za-z0-9_-]{2,}){2}$/;

  // We specify a minimum length of 20 characters because JWT strings
  // are usually longer than that and we want to avoid false positives.
  return typeof str === 'string' && str.length > 20 && jwtPattern.test(str);
}

function sanitizeFields(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    if (typeof obj === 'string' && isJWT(obj)) {
      return '[REDACTED JWT]';
    }
    return obj;
  }

  // Create a deep copy of the object so that we don't mutate the original object
  const copy = Array.isArray(obj) ? [...obj] : { ...obj };

  for (const key of Object.keys(copy)) {
    const lowerKey = key.toLowerCase();
    const value = copy[key];

    if (
      SENSITIVE_FIELD_KEY_SUBSTRINGS.some(
        (substring) =>
          // Fields that start with `$` are references to secrets (and not secrets themselves) so keep them in logs because they are useful.
          lowerKey.includes(substring) && !lowerKey.startsWith('$'),
      )
    ) {
      copy[key] = '[REDACTED]';
    } else if (typeof value === 'string' && isJWT(value)) {
      copy[key] = '[REDACTED JWT]';
    } else if (typeof value === 'object') {
      copy[key] = sanitizeFields(value);
    }
  }

  return copy;
}

try {
  const name = 'todesktop-cli';

  let parentDirectory: string;
  const homePath = os.homedir();
  if (process.platform === 'darwin') {
    parentDirectory = path.join(homePath, 'Library', 'Logs', name);
  } else if (process.platform === 'win32') {
    parentDirectory = path.join(
      process.env.APPDATA || path.join(homePath, 'AppData', 'Roaming'),
      name,
      'logs',
    );
  } else {
    parentDirectory = path.join(
      process.env.XDG_CONFIG_HOME || path.join(homePath, '.config'),
      name,
      'logs',
    );
  }

  fs.mkdirSync(parentDirectory, { recursive: true });

  logger = bunyan.createLogger({
    name,
    serializers: {
      scrub: (data: any) => sanitizeFields(data),
    },
    streams: [
      {
        level: 'debug',
        path: path.join(parentDirectory, 'main.log'),
      },
    ],
  });
} catch (e) {
  // Ignore

  const noOp = () => {};
  logger = {
    debug: noOp,
    error: noOp,
    fatal: noOp,
    info: noOp,
    trace: noOp,
    warn: noOp,
  };

  try {
    Sentry.captureException(e);
  } catch {
    // Ignore
  }
}

export default logger;

export interface Logger {
  debug: LogFn;
  error: LogFn;
  fatal: LogFn;
  info: LogFn;
  trace: LogFn;
  warn: LogFn;
}

export type LogFn = (...args: unknown[]) => void;
